import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { Observable, of } from 'rxjs';

import { TimeService } from '@core/common-legacy';
import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import {
  MemberPositionHistory,
  PositionAssignment,
  PositionAssignmentStatus,
  PositionHistoryDialogData,
  PositionHistoryDialogResult,
} from '@core/tellent-positions-types';
import { PositionHistoryEditCardComponent, PositionRowForm } from '@core/tellent-positions-ui';
import {
  AvatarComponent,
  BadgeComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogComponentI,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  IconComponent,
  Select2Item,
} from '@core/user-interface-legacy';

import { buildPositionHistoryMock } from './position-history.mock';

@Component({
  selector: 'tlc-position-history-dialog',
  templateUrl: './position-history-dialog.component.html',
  styleUrls: ['./position-history-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AvatarComponent,
    BadgeComponent,
    ButtonComponent,
    DialogBodyComponent,
    DialogHeaderComponent,
    IconComponent,
    PositionHistoryEditCardComponent,
    TranslatePipe,
  ],
})
export class PositionHistoryDialogComponent implements DialogComponentI<
  PositionHistoryDialogData,
  PositionHistoryDialogResult
> {
  private readonly dialogRef = inject(DialogRef);
  private readonly timeService = inject(TimeService);
  private readonly positionsFacade = inject(TellentPositionsDataFacade);
  private readonly t = injectTranslate();
  private readonly dialogService = inject(DialogService);
  private readonly storePositions = this.positionsFacade.positionsData;

  protected readonly history = signal<MemberPositionHistory | null>(null);
  protected readonly canEdit = signal(true);
  protected readonly mode = signal<'view' | 'edit'>('view');
  protected readonly isSaving = signal(false);

  protected readonly isEdit = computed(() => this.mode() === 'edit');

  protected readonly memberName = computed(() => this.history()?.member.fullName ?? '');
  protected readonly memberAvatarUrl = computed(
    () => this.history()?.member.avatarThumbUrl ?? null,
  );

  protected readonly currentRow = signal<PositionRowForm>(this.makeRow());
  protected readonly previous = new FormArray<PositionRowForm>([]);
  protected readonly scheduledRow = signal<PositionRowForm | null>(null);

  protected readonly positionItems = computed<Array<Select2Item<string>>>(() => {
    const items = new Map<string, Select2Item<string>>();
    const history = this.history();
    if (history) {
      const assignments = [history.scheduled, history.current, ...history.previous];
      for (const assignment of assignments) {
        if (assignment?.positionGuid) {
          items.set(assignment.positionGuid, {
            value: assignment.positionGuid,
            label: assignment.positionName,
          });
        }
      }
    }
    for (const position of this.storePositions()) {
      items.set(position.guid, { value: position.guid, label: position.name });
    }
    return [...items.values()];
  });

  constructor() {
    this.positionsFacade.fetchPositions();
  }

  public setDialogData(data: PositionHistoryDialogData): void {
    this.canEdit.set(data.canEdit ?? true);
    this.history.set(buildPositionHistoryMock(data.memberGuid));
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () => (this.isEdit() && !this.isSaving() ? this.confirmLeave() : of(true)),
    };
  }

  protected startedLabel(assignment: PositionAssignment): string {
    if (!assignment.startDate) {
      return this.t('tlc.positions.position_history_dialog.no_start_date');
    }
    return this.t('tlc.positions.position_history_dialog.started', {
      date: this.timeService.format({ input: assignment.startDate, pattern: 'D MMM YYYY' }),
    });
  }

  protected enterEdit(): void {
    const history = this.history();
    if (!history) return;

    this.currentRow.set(this.makeRow(history.current));
    this.previous.clear();
    for (const assignment of history.previous) {
      this.previous.push(this.makeRow(assignment));
    }
    this.scheduledRow.set(history.scheduled ? this.makeRow(history.scheduled) : null);
    this.mode.set('edit');
  }

  protected cancelEdit(): void {
    this.mode.set('view');
  }

  protected save(): void {
    // TODO(positions-history): persist via backend once the endpoint is finalized.
    this.isSaving.set(true);
    this.dialogRef.close({ saved: true, history: this.collectResult() });
  }

  protected scheduleChange(): void {
    this.scheduledRow.set(this.makeRow());
  }

  protected removeScheduled(): void {
    this.scheduledRow.set(null);
  }

  protected addPrevious(): void {
    this.previous.push(this.makeRow());
  }

  protected removePrevious(index: number): void {
    this.previous.removeAt(index);
  }

  private makeRow(assignment?: PositionAssignment | null): PositionRowForm {
    return new FormGroup({
      positionGuid: new FormControl<string | null>(assignment?.positionGuid ?? null),
      startDate: new FormControl<string | null>(assignment?.startDate ?? null),
    });
  }

  private collectResult(): MemberPositionHistory {
    const history = this.history();
    const toAssignment = (
      row: PositionRowForm,
      status: PositionAssignmentStatus,
      id: string,
    ): PositionAssignment => {
      const positionGuid = row.controls.positionGuid.value;
      return {
        id,
        positionGuid,
        positionName: this.positionNameByGuid(positionGuid),
        startDate: row.controls.startDate.value,
        status,
      };
    };

    const scheduled = this.scheduledRow();
    return {
      member: history?.member ?? { guid: '', fullName: '', avatarThumbUrl: null },
      scheduled: scheduled
        ? toAssignment(scheduled, PositionAssignmentStatus.Scheduled, 'scheduled')
        : null,
      current: toAssignment(this.currentRow(), PositionAssignmentStatus.Current, 'current'),
      previous: this.previous.controls.map((row, index) =>
        toAssignment(row, PositionAssignmentStatus.Previous, `previous-${index}`),
      ),
    };
  }

  private positionNameByGuid(guid: string | null): string {
    if (!guid) return '';
    return this.positionItems().find(item => item.value === guid)?.label ?? '';
  }

  private confirmLeave(): Observable<boolean> {
    return this.dialogService.error({
      title: this.t('tlc.positions.position_history_dialog.leave_confirmation.title'),
      message: this.t('tlc.positions.position_history_dialog.leave_confirmation.message'),
      agreeLabel: this.t('tlc.positions.position_history_dialog.leave_confirmation.confirm'),
      isCancelable: true,
    });
  }
}
